import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useI18n } from "@wato/i18n/react";
import type { MessageKey } from "@wato/i18n";
import { useApi } from "../../api/context";
import { filterNav } from "../workspace/nav-config";
import { quickActions } from "./quick-actions";
import { isPaletteShortcut, rankEntries, type PaletteEntry, type PaletteKind } from "./ranking";

const KIND_LABEL: Record<PaletteKind, MessageKey> = {
  action: "command.kind.action",
  page: "command.kind.page",
  contact: "command.kind.contact",
  conversation: "command.kind.conversation",
  product: "command.kind.product",
  flow: "command.kind.flow",
  template: "command.kind.template",
};

type Gate = Parameters<typeof filterNav>[0];

/**
 * App-wide Ctrl+K / Cmd+K palette: jump to any page the member can see, or search contacts, flows
 * and templates of the active workspace. Remote lookups use the existing list endpoints (RLS and
 * permissions apply as usual) and a failing source simply contributes nothing.
 */
export function GlobalCommandPalette({
  workspaceId,
  gate,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  gate: Gate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, dir } = useI18n();
  const api = useApi();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState<PaletteEntry[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isPaletteShortcut(event)) {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else {
      setQuery("");
      setRemote([]);
    }
  }, [open]);

  const pages = useMemo<PaletteEntry[]>(() => {
    const nav = filterNav(gate);
    const sectionPages = Object.values(nav.sections).flat();
    const topOnly = nav.top.filter((i) => !sectionPages.some((p) => p.to === i.to));
    const seen = new Set<string>();
    const hiddenTo = new Set(nav.notEnabled.map((i) => i.to));
    return [...topOnly, ...sectionPages, ...nav.notEnabled]
      .filter((item) => !seen.has(item.to) && seen.add(item.to))
      .map((item) => {
        // ACT-04: a page the account type hides stays findable, with a muted «not enabled» note.
        if (hiddenTo.has(item.to) && !sectionPages.some((p) => p.to === item.to)) {
          return {
            id: `page:${item.to}`,
            kind: "page" as const,
            label: t(item.label),
            hint: t("command.notEnabled"),
            to: item.to === "" ? `/w/${workspaceId}` : `/w/${workspaceId}/${item.to}`,
          };
        }
        // A locked page opens the page that explains how to unlock it, with the reason as the hint.
        const to = item.locked?.to ?? item.to;
        return {
          id: `page:${item.to}`,
          kind: "page" as const,
          label: t(item.label),
          ...(item.locked ? { hint: t(item.locked.reasonKey) } : {}),
          to: to === "" ? `/w/${workspaceId}` : `/w/${workspaceId}/${to}`,
        };
      });
  }, [gate, t, workspaceId]);

  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setRemote([]);
      return;
    }
    let stale = false;
    const timer = setTimeout(() => {
      const settle = <T,>(p: Promise<T>, map: (v: T) => PaletteEntry[]) =>
        p.then(map, () => [] as PaletteEntry[]);
      void Promise.all([
        // OWN-36: one tenant-scoped call for contacts + conversation text + products.
        settle(
          api.globalSearch ? api.globalSearch(workspaceId, q, 5) : Promise.reject(new Error("n/a")),
          (r) => [
            ...r.contacts.map((c) => ({
              id: `contact:${c.id}`,
              kind: "contact" as const,
              label: c.name ?? c.phone ?? c.id,
              ...(c.name && c.phone ? { hint: c.phone } : {}),
              to: `/w/${workspaceId}/contacts/${c.id}`,
            })),
            ...r.conversations.map((c) => ({
              id: `conversation:${c.id}`,
              kind: "conversation" as const,
              label: c.snippet,
              hint: c.contactName ?? c.contactPhone ?? "",
              to: `/w/${workspaceId}/conversations?c=${encodeURIComponent(c.id)}`,
            })),
            ...r.products.map((p) => ({
              id: `product:${p.id}`,
              kind: "product" as const,
              label: p.name,
              ...(p.sku ? { hint: p.sku } : {}),
              to: `/w/${workspaceId}/catalog`,
            })),
          ],
        ),
        settle(
          api.globalSearch
            ? Promise.resolve({ items: [], nextCursor: null })
            : api.listContacts(workspaceId, { q, limit: 5 }),
          (page) =>
            page.items.map((c) => ({
              id: `contact:${c.id}`,
              kind: "contact" as const,
              label: c.name ?? c.phoneE164 ?? c.username ?? c.id,
              ...(c.phoneE164 ? { hint: c.phoneE164 } : {}),
              to: `/w/${workspaceId}/contacts/${c.id}`,
            })),
        ),
        settle(api.listFlows(workspaceId, { q }), (flows) =>
          flows.slice(0, 5).map((f) => ({
            id: `flow:${f.id}`,
            kind: "flow" as const,
            label: f.name,
            to: `/w/${workspaceId}/flows/${f.id}`,
          })),
        ),
        settle(api.listTemplates(workspaceId), (templates) =>
          templates
            .filter((tpl) => tpl.name.toLowerCase().includes(q.toLowerCase()))
            .slice(0, 5)
            .map((tpl) => ({
              id: `template:${tpl.id}`,
              kind: "template" as const,
              label: tpl.name,
              hint: tpl.language,
              to: `/w/${workspaceId}/templates/${tpl.id}`,
            })),
        ),
      ]).then((groups) => {
        if (!stale) setRemote(groups.flat());
      });
    }, 250);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [api, open, query, workspaceId]);

  const results = useMemo(() => {
    // Remote rows already matched server-side (or by name): rank pages, keep remote as returned.
    const ranked = rankEntries([...quickActions(workspaceId, t), ...pages], query, 8);
    return [...ranked, ...remote];
  }, [pages, query, remote, t, workspaceId]);

  useEffect(() => setActive(0), [query]);

  if (!open) return null;
  const pick = (entry: PaletteEntry) => {
    onOpenChange(false);
    navigate(entry.to);
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("command.title")}
      dir={dir}
      data-testid="global-command-palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-fg/30 pt-24"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") onOpenChange(false);
        else if (event.key === "ArrowDown") {
          event.preventDefault();
          setActive((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          setActive((i) => Math.max(i - 1, 0));
        } else if (event.key === "Enter" && results[active]) pick(results[active]);
      }}
    >
      <div className="flex max-h-[28rem] w-[min(32rem,92vw)] flex-col gap-2 overflow-y-auto rounded-lg border border-line-strong bg-surface p-3 shadow-lg">
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={t("command.placeholder")}
          placeholder={t("command.placeholder")}
          className="rounded-md border border-line-strong bg-surface px-3 py-2 text-sm text-fg"
        />
        {results.length === 0 ? (
          <p className="text-sm text-fg-muted">{t("command.empty")}</p>
        ) : (
          <ul role="listbox" className="flex flex-col">
            {results.map((entry, i) => (
              <li
                key={entry.id}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(entry)}
                className={`flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 text-sm text-fg ${
                  i === active ? "bg-surface-sunken" : ""
                }`}
              >
                <span>
                  {entry.label}
                  {entry.hint ? <span className="ms-2 text-fg-muted">{entry.hint}</span> : null}
                </span>
                <span className="text-xs text-fg-muted">{t(KIND_LABEL[entry.kind])}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-fg-muted">{t("command.hint")}</p>
      </div>
    </div>
  );
}
